<template>
  <div class="flex flex-col w-full h-full overflow-hidden" dir="rtl">
    <!-- Chat area -->
    <div ref="chatArea" class="flex-1 overflow-y-auto px-4 pt-4 pb-4 space-y-3 scroll-smooth">
      <TransitionGroup name="bubble">
        <template v-for="(msg, i) in messages" :key="'msg-' + i">
          <!-- Text bubble -->
          <div
            v-if="msg.type === 'text'"
            class="flex"
            :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <div
              class="max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed"
              :class="msg.role === 'user'
                ? 'bg-violet-100 text-slate-800 rounded-tl-md'
                : 'bg-slate-50 border border-slate-200 text-slate-700 rounded-tr-md'"
            >
              {{ msg.content }}
            </div>
          </div>

          <!-- Employee mode picker -->
          <div v-else-if="msg.type === 'employee-mode'" class="flex justify-start">
            <div class="max-w-[90%] flex flex-col gap-2">
              <button
                class="px-5 py-3 rounded-xl border text-sm font-medium transition-all text-right"
                :class="employeeServiceMode === 'same' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-purple-300'"
                @click="selectEmployeeMode('same')"
              >
                כולם מציעים את אותם שירותים
              </button>
              <button
                class="px-5 py-3 rounded-xl border text-sm font-medium transition-all text-right"
                :class="employeeServiceMode === 'different' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-purple-300'"
                @click="selectEmployeeMode('different')"
              >
                לכל עובד/ת יש התמחות שונה
              </button>
            </div>
          </div>

          <!-- Color picker card -->
          <div v-else-if="msg.type === 'color-picker'" class="flex justify-start">
            <div class="max-w-[90%]">
              <OnboardingColorPicker v-model="preferredColor" />
            </div>
          </div>

          <!-- Logo upload card -->
          <div v-else-if="msg.type === 'logo-upload'" class="flex justify-start">
            <div class="max-w-[90%]">
              <OnboardingLogoUpload v-model="logoUrl" />
            </div>
          </div>

          <!-- Working hours card -->
          <div v-else-if="msg.type === 'working-hours'" class="flex justify-start">
            <div class="w-full max-w-sm">
              <OnboardingWorkingHoursCard v-model="workingHours" />
            </div>
          </div>

          <!-- Services upload card -->
          <div v-else-if="msg.type === 'services-upload'" class="flex justify-start">
            <div class="w-full max-w-sm">
              <OnboardingServicesUpload v-model="serviceFiles" />
            </div>
          </div>

          <!-- Site link card (shown after applySetup) -->
          <div v-else-if="msg.type === 'site-link'" class="flex justify-start">
            <div class="w-full max-w-sm">
              <OnboardingSiteLinkCard :url="siteUrl!" />
            </div>
          </div>

          <!-- Progress bubble -->
          <OnboardingProgressBubble v-else-if="msg.type === 'progress'" :label="msg.content" />

          <!-- Services card -->
          <div v-else-if="msg.type === 'services-card'" class="flex justify-start">
            <div class="w-full max-w-sm">
              <OnboardingServicesCard
                :services="proposal!.services"
                @toggle-service="toggleService"
                @edit-services="openEditChat('services')"
                @update-service="updateService"
                @set-image="setServiceImage"
              />
            </div>
          </div>

          <!-- Features card -->
          <div v-else-if="msg.type === 'features-card'" class="flex justify-start">
            <div class="w-full max-w-sm">
              <OnboardingFeaturesCard
                :features="proposal!.features"
                @toggle-feature="toggleFeature"
              />
            </div>
          </div>

          <!-- Site content card -->
          <div v-else-if="msg.type === 'site-content-card'" class="flex justify-start">
            <div class="w-full max-w-sm">
              <OnboardingSiteContentCard
                :siteContent="proposal!.siteContent"
                @update-subtitle="updateSubtitle"
                @update-about="updateAbout"
              />
            </div>
          </div>
        </template>
      </TransitionGroup>

      <!-- Loading indicator -->
      <div v-if="isLoading" class="flex justify-start">
        <div class="px-4 py-3 rounded-2xl rounded-tr-md bg-slate-50 border border-slate-200">
          <span class="flex gap-1.5 items-center">
            <span class="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style="animation-delay:0s" />
            <span class="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style="animation-delay:0.15s" />
            <span class="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style="animation-delay:0.3s" />
          </span>
        </div>
      </div>
    </div>

    <!-- Bottom area: "Let's go" button or input -->
    <div class="shrink-0 px-4 pb-5 pt-3">
      <!-- Final action button (after proposal is shown) -->
      <div v-if="phase === 'interactive'" class="flex flex-col gap-3">
        <!-- Retry button when generation failed -->
        <button
          v-if="generationFailed && !proposal"
          class="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 text-white font-bold py-4 rounded-xl border-0 transition-all shadow-lg shadow-pink-500/30 flex items-center justify-center gap-2"
          @click="retryGeneration"
        >
          <RefreshCw class="w-5 h-5" />
          ניסיון שוב
        </button>

        <!-- Normal interactive mode when proposal exists -->
        <template v-if="proposal">
          <div class="flex gap-2">
            <div class="relative flex-1">
              <input
                ref="chatInputRef"
                v-model="userInput"
                type="text"
                class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-pink-400 transition-colors"
                placeholder="יש לך שינויים? כתבו כאן..."
                @keyup.enter="sendChatMessage"
              />
            </div>
            <button
              v-if="userInput.trim() || isLoading"
              class="bg-pink-500 text-white rounded-xl px-4 py-3 text-sm font-medium hover:bg-pink-400 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center min-w-[52px]"
              :disabled="isLoading"
              @click="sendChatMessage"
            >
              <span v-if="isLoading" class="flex gap-1 items-center">
                <span class="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style="animation-delay:0s" />
                <span class="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style="animation-delay:0.15s" />
                <span class="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style="animation-delay:0.3s" />
              </span>
              <Send v-else class="w-4 h-4" />
            </button>
          </div>
          <button
            class="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 text-white font-bold py-4 rounded-xl border-0 transition-all shadow-lg shadow-pink-500/30 transform hover:-translate-y-0.5 flex items-center justify-center text-lg gap-2"
            :disabled="isApplying"
            @click="applyAndFinish"
          >
            <Rocket v-if="!isApplying" class="w-5 h-5" />
            <span v-if="isApplying" class="flex gap-1.5 items-center">
              <span class="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style="animation-delay:0s" />
              <span class="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style="animation-delay:0.15s" />
              <span class="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style="animation-delay:0.3s" />
            </span>
            <span v-else>יאללה, מתחילים!</span>
          </button>
        </template>
      </div>

      <!-- Description input (question phase) -->
      <div v-else-if="phase === 'asking-description'" class="flex flex-col gap-2">
        <textarea
          v-model="businessDescription"
          rows="3"
          class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-pink-400 transition-colors resize-none"
          placeholder="ספרו בקצרה על העסק שלכם..."
          @keyup.enter.exact.prevent="submitDescription"
        />
        <button
          class="w-full bg-pink-500 hover:bg-pink-400 text-white rounded-xl px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2"
          @click="submitDescription"
        >
          <Send class="w-4 h-4" />
          המשך
        </button>
      </div>

      <!-- Employee mode: nothing here, buttons are in the chat -->
      <div v-else-if="phase === 'asking-employee-mode'" />

      <!-- Color confirmation -->
      <div v-else-if="phase === 'asking-color'" class="flex justify-center">
        <button
          class="bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold py-3 px-8 rounded-xl transition-all hover:from-pink-400 hover:to-purple-500"
          @click="submitColor"
        >
          {{ preferredColor ? 'יופי, קדימה!' : 'דלגו על בחירת צבע' }}
        </button>
      </div>

      <!-- Logo confirmation / skip -->
      <div v-else-if="phase === 'asking-logo'" class="flex justify-center">
        <button
          class="bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold py-3 px-8 rounded-xl transition-all hover:from-pink-400 hover:to-purple-500"
          @click="submitLogo"
        >
          {{ logoUrl ? 'מעולה, קדימה!' : 'דלגו, אעלה אחר כך' }}
        </button>
      </div>

      <!-- Working hours confirmation -->
      <div v-else-if="phase === 'asking-working-hours'" class="flex justify-center">
        <button
          class="bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold py-3 px-8 rounded-xl transition-all hover:from-pink-400 hover:to-purple-500"
          @click="submitWorkingHours"
        >
          יופי, המשך
        </button>
      </div>

      <!-- Services upload confirmation -->
      <div v-else-if="phase === 'asking-services-upload'" class="flex justify-center">
        <button
          class="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 text-white font-bold py-3 px-8 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-70"
          :disabled="isExtracting"
          @click="submitServicesUpload"
        >
          <span v-if="isExtracting" class="flex gap-1.5 items-center">
            <span class="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style="animation-delay:0s" />
            <span class="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style="animation-delay:0.15s" />
            <span class="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style="animation-delay:0.3s" />
          </span>
          <span v-else>{{ serviceFiles.length ? 'חילוץ השירותים מהרשימה' : 'דלגו, אוסיף שירותים אחר כך' }}</span>
        </button>
      </div>

      <!-- Done — site link shown, single CTA to enter dashboard -->
      <div v-else-if="phase === 'done'" class="flex justify-center">
        <button
          class="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 text-white font-bold py-4 rounded-xl border-0 transition-all shadow-lg shadow-pink-500/30 flex items-center justify-center gap-2 text-lg"
          @click="goToDashboard"
        >
          <Rocket class="w-5 h-5" />
          כניסה לניהול העסק
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import {
  IOnboardingProposal,
  IOnboardingSetupRequest,
  IExtractedService,
  IAvailability,
  BusinessCategoryEnum,
  BusinessSizeEnum,
  CurrentBookingMethodEnum,
  businessCategoryLabels,
  EmployeeServiceMode,
} from '@torim-app/types';
import { OnboardingApi } from '@app/api/admin/onboarding-api';
import { AdminRouteNames } from '@app/router/route-names';
import { trackEvent } from '@app/composables/posthog';
import { Send, Rocket, RefreshCw } from 'lucide-vue-next';
import OnboardingColorPicker from './OnboardingColorPicker.vue';
import OnboardingLogoUpload from './OnboardingLogoUpload.vue';
import OnboardingServicesUpload from './OnboardingServicesUpload.vue';
import OnboardingProgressBubble from './OnboardingProgressBubble.vue';
import OnboardingServicesCard from './OnboardingServicesCard.vue';
import OnboardingFeaturesCard from './OnboardingFeaturesCard.vue';
import OnboardingSiteContentCard from './OnboardingSiteContentCard.vue';
import OnboardingWorkingHoursCard from './OnboardingWorkingHoursCard.vue';
import OnboardingSiteLinkCard from './OnboardingSiteLinkCard.vue';

const props = defineProps<{
  businessName: string;
  businessCategory: BusinessCategoryEnum[];
  businessSize: BusinessSizeEnum;
  currentBookingMethod: CurrentBookingMethodEnum;
}>();

const emit = defineEmits<{
  skip: [];
}>();

const router = useRouter();
const toast = useToast();

interface ChatMessage {
  type: 'text' | 'color-picker' | 'logo-upload' | 'employee-mode' | 'progress' | 'services-card' | 'features-card' | 'site-content-card' | 'working-hours' | 'services-upload' | 'site-link';
  role: 'user' | 'assistant';
  content: string;
}

const chatArea = ref<HTMLElement>();
const chatInputRef = ref<HTMLInputElement>();
const messages = ref<ChatMessage[]>([]);
const phase = ref<'greeting' | 'asking-description' | 'asking-color' | 'asking-logo' | 'asking-employee-mode' | 'asking-working-hours' | 'asking-services-upload' | 'generating' | 'interactive' | 'done'>('greeting');
const employeeServiceMode = ref<EmployeeServiceMode | null>(null);
const isLoading = ref(false);
const isApplying = ref(false);
const isExtracting = ref(false);
const userInput = ref('');
const businessDescription = ref('');
const preferredColor = ref('#8864ee');
const logoUrl = ref<string | null>(null);
const workingHours = ref<Partial<IAvailability>>({});
const serviceFiles = ref<File[]>([]);
const extractedServices = ref<IExtractedService[]>([]);
const proposal = ref<IOnboardingProposal | null>(null);
const generationFailed = ref(false);
const siteUrl = ref<string | null>(null);
const conversationHistory = ref<Array<{ role: 'user' | 'assistant'; content: string }>>([]);

const scrollToBottom = async () => {
  await nextTick();
  if (chatArea.value) {
    chatArea.value.scrollTop = chatArea.value.scrollHeight;
  }
};

// True when the user is already at (or very close to) the bottom. Read BEFORE pushing a
// message so we don't yank the user down while they're reading earlier content.
const isNearBottom = () => {
  const el = chatArea.value;
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight < 140;
};

const addMessage = async (msg: ChatMessage, delay = 0, force = false) => {
  if (delay > 0) {
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  const stick = force || isNearBottom();
  messages.value.push(msg);
  if (stick) await scrollToBottom();
};

const categoryLabel = props.businessCategory.map(c => businessCategoryLabels[c]).filter(Boolean).join(', ') || 'העסק';

const addAssistantMessage = async (content: string, delay = 0) => {
  await addMessage({ type: 'text', role: 'assistant', content }, delay);
  conversationHistory.value.push({ role: 'assistant', content });
};

// Start the conversation flow
onMounted(async () => {
  trackEvent('ai_onboarding_started', { businessCategory: props.businessCategory });

  // Greeting
  await addAssistantMessage(`היי! 👋 אני העוזר החכם של טיימאיט. אני רואה שאתם מתמחים ב${categoryLabel} . מגניב!`, 500);
  await addAssistantMessage('אני אעזור לכם להקים את העסק בדקה. ספרו לי קצת על מה שאתם עושים. איזה שירותים, סגנון, מה מיוחד אצלך?', 800);

  phase.value = 'asking-description';
});

const submitDescription = async () => {
  if (!businessDescription.value.trim()) {
    businessDescription.value = '';
    // User skipped - that's fine, proceed without description
  }

  const desc = businessDescription.value.trim();
  if (desc) {
    await addMessage({ type: 'text', role: 'user', content: desc });
    conversationHistory.value.push({ role: 'user', content: desc });
  }

  await addAssistantMessage('מעולה! עכשיו בואו נבחר צבע מוביל לאתר שלכם 🎨', 300);

  await addMessage({ type: 'color-picker', role: 'assistant', content: '' }, 300);

  phase.value = 'asking-color';
};

const submitColor = async () => {
  await addAssistantMessage('אם יש לכם לוגו, זה הזמן להעלות אותו 🖼️ אפשר גם לדלג ולהוסיף אחר כך.', 300);
  await addMessage({ type: 'logo-upload', role: 'assistant', content: '' }, 300);
  phase.value = 'asking-logo';
};

const submitLogo = async () => {
  // If multi-employee business, ask about service differentiation first
  if (props.businessSize !== BusinessSizeEnum.SOLO) {
    phase.value = 'asking-employee-mode';
    await addAssistantMessage('אני רואה שיש לכם צוות! שאלה חשובה — האם כל העובדים מציעים את אותם שירותים, או שלכל אחד יש התמחות שונה?', 400);
    await addMessage({ type: 'employee-mode', role: 'assistant', content: '' }, 300);
    return;
  }

  await askWorkingHours();
};

const selectEmployeeMode = async (mode: EmployeeServiceMode) => {
  employeeServiceMode.value = mode;
  const label = mode === 'same' ? 'כולם מציעים את אותם שירותים' : 'לכל אחד התמחות שונה';
  await addMessage({ type: 'text', role: 'user', content: label });
  await askWorkingHours();
};

const askWorkingHours = async () => {
  phase.value = 'asking-working-hours';
  await addAssistantMessage('עוד שאלה קטנה ⏰ — מתי אתם עובדים? סמנו את הימים ובחרו שעות.', 300);
  await addMessage({ type: 'working-hours', role: 'assistant', content: '' }, 300);
};

const submitWorkingHours = async () => {
  // Summarize selected days for the user bubble
  const dayLabels: Record<string, string> = {
    sunday: 'א׳', monday: 'ב׳', tuesday: 'ג׳', wednesday: 'ד׳', thursday: 'ה׳', friday: 'ו׳', saturday: 'ש׳',
  };
  const activeDays = Object.entries(workingHours.value)
    .filter(([, slots]) => Array.isArray(slots) && slots.length > 0)
    .map(([day]) => dayLabels[day] || day);
  const firstSlot = Object.values(workingHours.value).find(s => Array.isArray(s) && s.length > 0) as { from: string; to: string }[] | undefined;
  if (activeDays.length && firstSlot?.[0]) {
    await addMessage({
      type: 'text',
      role: 'user',
      content: `${activeDays.join(', ')} · ${firstSlot[0].from}–${firstSlot[0].to}`,
    });
  }
  await askServicesUpload();
};

const askServicesUpload = async () => {
  phase.value = 'asking-services-upload';
  await addAssistantMessage('כמעט סיימנו! יש לכם רשימת שירותים ומחירים? 📋 העלו תמונה או קובץ PDF ואני אחלץ ממנו את כל השירותים אוטומטית.', 300);
  await addMessage({ type: 'services-upload', role: 'assistant', content: '' }, 300);
};

const submitServicesUpload = async () => {
  if (serviceFiles.value.length) {
    isExtracting.value = true;
    await addMessage({ type: 'text', role: 'user', content: `העליתי ${serviceFiles.value.length} קבצים 📎` });
    try {
      const formData = new FormData();
      for (const file of serviceFiles.value) {
        formData.append('files', file);
      }
      extractedServices.value = await OnboardingApi.extractServices(formData);
      trackEvent('ai_onboarding_services_extracted', { count: extractedServices.value.length });

      if (!extractedServices.value.length) {
        await addAssistantMessage('לא הצלחתי לזהות שירותים בקבצים. נמשיך, ותוכלו להוסיף שירותים בקלות אחר כך 🙏', 200);
      }
    } catch (error) {
      console.error('Failed to extract services:', error);
      extractedServices.value = [];
      await addAssistantMessage('הייתה בעיה בקריאת הקבצים. נמשיך, ותוכלו להוסיף שירותים אחר כך 🙏', 200);
    } finally {
      isExtracting.value = false;
    }
  } else {
    extractedServices.value = [];
  }

  await startGenerating();
};

const startGenerating = async () => {
  phase.value = 'generating';

  await addAssistantMessage('מצוין! תנו לי רגע, אני בונה לכם הצעה מותאמת אישית... ✨', 300);

  // Show progress bubbles
  // Start API call immediately, show progress bubbles as user waits
  const request: IOnboardingSetupRequest = {
    businessCategory: props.businessCategory,
    businessSize: props.businessSize,
    currentBookingMethod: props.currentBookingMethod,
    businessDescription: businessDescription.value || undefined,
    preferredColor: preferredColor.value || undefined,
    employeeServiceMode: employeeServiceMode.value || undefined,
    extractedServices: extractedServices.value.length ? extractedServices.value : undefined,
    skipServiceGeneration: extractedServices.value.length ? undefined : true,
  };

  const apiCall = OnboardingApi.generateSetup(request);

  // Show progress bubbles during the actual wait
  const bubbles = [
    { delay: 600, content: 'מארגן את השירותים שלכם...' },
    { delay: 1600, content: 'בוחר פיצ׳רים מתאימים...' },
    { delay: 2800, content: 'כותב תוכן לאתר שלכם...' },
  ];
  const bubbleTimers = bubbles.map(({ delay, content }) =>
    setTimeout(() => {
      // Only add if still generating (phase not changed)
      if (phase.value === 'generating') {
        const stick = isNearBottom();
        messages.value.push({ type: 'progress', role: 'assistant', content });
        if (stick) scrollToBottom();
      }
    }, delay),
  );

  try {
    proposal.value = await apiCall;
    // Clear bubble timers that haven't fired yet
    bubbleTimers.forEach(clearTimeout);
    trackEvent('ai_onboarding_proposal_generated');

    // Remove progress bubbles
    messages.value = messages.value.filter(m => m.type !== 'progress');

    // Show the proposal
    await addAssistantMessage(`הנה מה שהכנתי לך! עברו על ההצעה, שנו מה שרוצים, ותלחצו ״יאללה, מתחילים!״ 👇`, 300);

    await addMessage({ type: 'services-card', role: 'assistant', content: '' }, 400);
    await addMessage({ type: 'features-card', role: 'assistant', content: '' }, 400);
    await addMessage({ type: 'site-content-card', role: 'assistant', content: '' }, 400);

    phase.value = 'interactive';
  } catch (error) {
    console.error('Failed to generate proposal:', error);
    bubbleTimers.forEach(clearTimeout);
    messages.value = messages.value.filter(m => m.type !== 'progress');
    await addAssistantMessage('אופס, משהו לא עבד כשניסיתי ליצור את ההצעה. תוכלו לנסות שוב 👇');
    generationFailed.value = true;
    phase.value = 'interactive';
  }
};

const toggleService = (tempId: string) => {
  if (!proposal.value) return;
  const service = proposal.value.services.find(s => s.tempId === tempId);
  if (service) service.enabled = !service.enabled;
};

const updateService = (tempId: string, changes: { name: string; duration: number; price: number; category?: string }) => {
  if (!proposal.value) return;
  const service = proposal.value.services.find(s => s.tempId === tempId);
  if (service) {
    service.name = changes.name;
    service.duration = changes.duration;
    service.price = changes.price;
    service.category = changes.category;
  }
};

const setServiceImage = (tempId: string, imageUrl: string) => {
  if (!proposal.value) return;
  const service = proposal.value.services.find(s => s.tempId === tempId);
  if (service) service.imageUrl = imageUrl;
};

const toggleFeature = (featureKey: string) => {
  if (!proposal.value) return;
  const feature = proposal.value.features.find(f => f.feature === featureKey);
  if (feature) feature.enabled = !feature.enabled;
};

const updateSubtitle = (value: string) => {
  if (proposal.value) proposal.value.siteContent.subTitle = value;
};

const updateAbout = (value: string) => {
  if (proposal.value) proposal.value.siteContent.about = value;
};

const retryGeneration = async () => {
  generationFailed.value = false;
  // Remove error message from chat
  messages.value = messages.value.filter(m => !(m.type === 'text' && m.role === 'assistant' && m.content.includes('אופס')));
  await startGenerating();
};

const openEditChat = async (topic: string) => {
  const prompts: Record<string, string> = {
    services: 'אני רוצה לשנות את השירותים — ',
    features: 'אני רוצה לשנות את הפיצ\'רים — ',
    siteContent: 'אני רוצה לשנות את תוכן האתר — ',
  };
  userInput.value = prompts[topic] ?? '';
  await nextTick();
  chatInputRef.value?.focus();
};

const sendChatMessage = async () => {
  if (!userInput.value.trim() || !proposal.value || isLoading.value) return;

  const message = userInput.value.trim();
  userInput.value = '';

  await addMessage({ type: 'text', role: 'user', content: message }, 0, true);
  conversationHistory.value.push({ role: 'user', content: message });

  isLoading.value = true;

  try {
    const response = await OnboardingApi.chat(message, proposal.value, conversationHistory.value);

    if (response.updatedProposal) {
      proposal.value = response.updatedProposal;
    }

    await addMessage({ type: 'text', role: 'assistant', content: response.reply });
    conversationHistory.value.push({ role: 'assistant', content: response.reply });

    // proposal.value is already updated reactively — cards will re-render in-place
  } catch (error) {
    console.error('Chat error:', error);
    await addAssistantMessage('סליחה, לא הצלחתי לעבד את הבקשה. אפשר לנסות שוב או פשוט לערוך ידנית.');
  }

  isLoading.value = false;
};

const applyAndFinish = async () => {
  if (!proposal.value || isApplying.value) return;

  isApplying.value = true;
  trackEvent('ai_onboarding_applied');

  try {
    // Build feature configs from actionable features that have suggestedConfig
    const featureConfigs: Record<string, Record<string, any>> = {};
    for (const feat of proposal.value.features) {
      if (feat.enabled && feat.category === 'actionable' && feat.suggestedConfig) {
        featureConfigs[feat.feature] = feat.suggestedConfig;
      }
    }

    const result = await OnboardingApi.applySetup({
      services: proposal.value.services,
      features: proposal.value.features.filter(f => f.enabled && f.category === 'actionable').map(f => f.feature as string),
      siteContent: proposal.value.siteContent,
      preferredColor: preferredColor.value || undefined,
      logoUrl: logoUrl.value || undefined,
      employeeServiceMode: employeeServiceMode.value || undefined,
      featureConfigs: Object.keys(featureConfigs).length > 0 ? featureConfigs : undefined,
      workingHours: Object.keys(workingHours.value).length > 0 ? workingHours.value : undefined,
    });

    // Build the client site URL from the returned bid
    const domain = import.meta.env.VITE_BUSINESS_WEBSITE_DOMAIN;
    siteUrl.value = `${domain}/business/${result.bid}`;

    toast.success('העסק מוכן! ברוכים הבאים לטיימאיט 🎉');

    // Reveal the site link card + final CTA instead of navigating immediately
    await addAssistantMessage('הכל מוכן! 🎉 הקמתי לכם את העסק במערכת.', 300);
    await addMessage({ type: 'site-link', role: 'assistant', content: '' }, 400);
    await addAssistantMessage('תוכלו להיכנס לניהול העסק ולהתחיל לקבל תורים 👇', 400);

    phase.value = 'done';
    isApplying.value = false;
  } catch (error) {
    console.error('Apply error:', error);
    toast.error('לא הצלחנו להחיל את ההגדרות. אנא נסו שוב.');
    isApplying.value = false;
  }
};

const goToDashboard = async () => {
  await router.replace({ name: AdminRouteNames.Home });
};
</script>

<style scoped>
.bubble-enter-active {
  transition: all 0.3s ease-out;
}
.bubble-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
</style>
